import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LegalOverlay from './LegalOverlay';
import CookieConsent from './CookieConsent';

/* ──────────────────────────────────────────────────────────────
   Shared Footer — rendered at the bottom of every page via
   <ContactScoping />. 4-column responsive grid on desktop,
   stacked columns on mobile. Handles its own legal overlay state.
   ────────────────────────────────────────────────────────────── */

export default function Footer() {
  const [legalPage, setLegalPage] = useState<'privacy' | 'terms' | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // "Contact Us" scrolls to #contact if on a page with the form,
  // otherwise navigates home and lets the hash scroll handler take over.
  const handleContactClick = () => {
    const el = document.getElementById('contact');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#contact');
    }
  };

  // "Ready To Use Datasets" — on the homepage, scroll to the #datasets
  // section; elsewhere, route to /#datasets and let the home page handle it.
  const handleDatasetsClick = () => {
    if (location.pathname === '/') {
      const el = document.getElementById('datasets');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      else navigate('/#datasets');
    } else {
      navigate('/#datasets');
    }
  };

  const columnHeaderClass =
    'text-[9px] md:text-[clamp(8px,0.65vw,11px)] uppercase tracking-[0.2em] font-bold text-white/90 mb-2.5 md:mb-[clamp(12px,1vw,18px)] text-center md:text-left';
  const linkClass =
    'text-[11px] md:text-[clamp(10px,0.81vw,14px)] text-white/60 hover:text-[#56CFFC] transition-colors text-center md:text-left md:w-fit leading-snug';

  return (
    <footer className="w-full shrink-0 relative">
      {/* Smooth gradient bridge — in normal flow so transparent top reveals the page bg above */}
      <div className="h-16 md:h-48 bg-gradient-to-b from-transparent to-[#050505] pointer-events-none" />

      <div className="bg-[#050505] relative">
        <div className="max-w-[var(--max-content)] mx-auto px-[var(--px-container)] py-6 md:py-[clamp(32px,3vw,56px)]">
          {/* ── Top: brand panel + 2 nav columns. Mobile: brand spans full width on
              top, then Company / Solutions in a single 2-col row beneath.
              Desktop: 3-column grid. ── */}
          <div className="grid grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr] gap-y-6 md:gap-y-0 gap-x-6 md:gap-x-[clamp(24px,2.08vw,48px)] mb-6 md:mb-[clamp(32px,2.78vw,56px)]">
            {/* Column 1 — Brand. Inner wrapper is w-fit (hugs the logo width) so the
                tagline beneath can be w-full of that wrapper and justify-fill to match
                the logo width exactly. Spans both mobile cols, centered. */}
            <div className="col-span-2 md:col-span-1 flex flex-col items-center md:items-start">
              <div className="flex flex-col items-stretch w-fit">
                <Link to="/" className="inline-block">
                  <img
                    src="/voqals-logo.svg"
                    alt="Voqals Logo"
                    width="226"
                    height="64"
                    className="h-11 md:h-[clamp(44px,3.8vw,68px)] w-auto"
                    referrerPolicy="no-referrer"
                  />
                </Link>
                <span className="block w-full mt-2 md:mt-[clamp(8px,0.7vw,12px)] text-center md:text-justify md:[text-align-last:justify] text-[6px] md:text-[clamp(6px,0.52vw,9px)] uppercase tracking-[0.1em] md:tracking-[0.18em] font-bold text-white/55 whitespace-nowrap">
                  Ground Truth for Indian Speech AI
                </span>
              </div>
            </div>

            {/* Column 2 — Company */}
            <div className="flex flex-col items-center md:items-start">
              <h4 className={columnHeaderClass}>Company</h4>
              <div className="flex flex-col items-center md:items-start gap-2 md:gap-[clamp(8px,0.7vw,12px)]">
                <Link to="/audio-company-india/" className={linkClass}>India's Audio Company</Link>
                <Link to="/voice-talent/" className={linkClass}>Join as Voice Talent</Link>
                <Link to="/voice-talent/portal" className={linkClass}>Talent Login</Link>
                <button onClick={handleContactClick} className={linkClass}>Contact Us</button>
              </div>
            </div>

            {/* Column 3 — Solutions */}
            <div className="flex flex-col items-center md:items-start">
              <h4 className={columnHeaderClass}>Solutions</h4>
              <div className="flex flex-col items-center md:items-start gap-2 md:gap-[clamp(8px,0.7vw,12px)]">
                <Link to="/custom-data-collection/" className={linkClass}>Custom Data Collection</Link>
                <button onClick={handleDatasetsClick} className={linkClass}>Ready To Use Datasets</button>
              </div>
            </div>

          </div>

          {/* ── Divider ── */}
          <div className="h-px w-full bg-white/10 mb-4 md:mb-[clamp(18px,1.5vw,28px)]" />

          {/* ── SEO bio (kept; valuable keyword presence). Centered on mobile to
              match the rest of the footer columns. ── */}
          <p className="text-[10px] md:text-[clamp(8px,0.69vw,12px)] leading-snug md:leading-relaxed text-white/55 mb-4 md:mb-[clamp(16px,1.39vw,24px)] text-center md:text-left">
            Voqals is an India-based speech data company specializing in high-quality, code-switched Indian language datasets for ASR, TTS, and conversational AI. Our studio-certified, SFT-ready datasets cover Indian languages and are trusted by AI teams building for the world's most linguistically complex market.
          </p>

          {/* ── Bottom strip: copyright + legal links. Stacks vertically + centered
              on mobile, single-row on desktop. ── */}
          <div className="flex flex-col md:flex-row items-center md:items-center justify-between gap-2 md:gap-[clamp(16px,1.39vw,24px)]">
            <p className="text-[9px] md:text-[clamp(7px,0.64vw,11px)] text-white/55 text-center md:text-left">
              &copy; 2026 Voqals MediaTech Pvt. Ltd. All rights reserved.
            </p>
            <div className="flex items-center gap-4 md:gap-[clamp(16px,1.39vw,24px)] text-[10px] md:text-[clamp(7px,0.58vw,10px)] uppercase tracking-[0.15em] font-bold text-white/55">
              <button onClick={() => setLegalPage('privacy')} className="hover:text-white/80 transition-colors cursor-pointer">
                Privacy Policy
              </button>
              <span className="text-white/20">|</span>
              <button onClick={() => setLegalPage('terms')} className="hover:text-white/80 transition-colors cursor-pointer">
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </div>

      <LegalOverlay page={legalPage} onClose={() => setLegalPage(null)} />
      <CookieConsent onOpenPrivacy={() => setLegalPage('privacy')} />
    </footer>
  );
}
